import Carousel from "@components/Carousel";
import Container from "@components/Container";
import CurvedFooter from "@components/CurvedFooter";
import Header from "@components/headers/Header";
import PublicHeader from "@components/headers/PublicHeader";
import SkeletonPicture from "@components/Skeletons/SkeletonPicture";
import { useAuth } from "@lib/authContext";
import { useBenefits } from "@lib/hooks/useBenefits";
import { Benefit } from "@lib/hooks/usePlan";
import { BenefitInterface, useUser } from "@lib/hooks/useUser";
import Image from "next/image";
import React, { ReactNode, useEffect } from "react";
import Link from "next/link";
import { GoTriangleRight } from "react-icons/go";
import styles from "./coverageDetailsTemplate.module.scss";

interface AssistanceDetailsProps {
  slug: string;
  title: string;
  subtitle: string;
  iconPath: string;
  sumary?: string[];
  qttRelatedBenefits?: number;
  children: ReactNode;
}

const CoverageDetailsTemplate: React.FC<AssistanceDetailsProps> = ({
  slug,
  title,
  subtitle,
  iconPath,
  sumary,
  qttRelatedBenefits = 2,
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const { data: user, isSuccess: isUserLoaded } = useUser();
  const benefits = user?.subscription?.coverages
    .concat(user.subscription.assistances)
    .filter((benefit: BenefitInterface) => benefit.slug != slug);

  const allBenefits = useBenefits().data;

  const relatedBenefitsFiltered: Benefit[] =
    allBenefits?.filter((i) => i.slug !== slug) || [];
  const relatedBenefitsRandomized = relatedBenefitsFiltered.reduce<Benefit[]>(
    (acc, x) =>
      acc.length == qttRelatedBenefits
        ? acc
        : acc.concat(
            relatedBenefitsFiltered[
              Math.floor(Math.random() * relatedBenefitsFiltered.length)
            ]
          ),
    []
  );

  useEffect(() => {
    document.title = `Segurize | ${title}`;
  }, []);

  return (
    <div className={styles.container}>
      {isAuthenticated ? <Header flat fixed /> : <PublicHeader />}
      <div
        className={isAuthenticated ? styles.privateHeader : styles.publicHeader}
      >
        <Container>
          <h2>{title}</h2>
          <span>{subtitle}</span>
          <Image src={iconPath} width={100} height={100} alt={title} />
        </Container>
      </div>
      <Container className={styles.sumary}>
        {sumary && (
          <>
            <h3>Resumo</h3>
            <ul>
              {sumary.map((item, index) => (
                <li key={index}>
                  <GoTriangleRight />
                  <p>{item}</p>
                </li>
              ))}
            </ul>
            <hr />
          </>
        )}
        <div className={styles.mainContent}>{children}</div>
      </Container>
      <div className={styles.relatedContainer}>
        {isAuthenticated ? (
          <>
            <h3>Saiba mais sobre suas coberturas</h3>
            <div className={styles.relatedContent}>
              <Container>
                {isUserLoaded ? (
                  <Carousel>
                    {benefits!.map((benefit: BenefitInterface) => (
                      <a
                        key={benefit.slug}
                        href={benefit.slug}
                        className={styles.relatedCard}
                      >
                        <Image
                          src={benefit.icon}
                          width={100}
                          height={100}
                          alt={benefit.name}
                        />
                        <h5>{benefit.name}</h5>
                        <p>{benefit.description}</p>
                      </a>
                    ))}
                  </Carousel>
                ) : (
                  <>
                    <SkeletonPicture size="lg" />
                    <SkeletonPicture size="lg" />
                    <SkeletonPicture size="lg" />
                  </>
                )}
              </Container>
            </div>
          </>
        ) : (
          <>
            <h3>Conheça outras coberturas</h3>
            <Container className={styles.defaultRelatedContent}>
              {relatedBenefitsRandomized?.map((benefit, index) => (
                <Link key={index} href={"/benefits/" + benefit.slug}>
                  <a className={styles.relatedCard}>
                    <Image
                      src={benefit.icon}
                      width={100}
                      height={100}
                      alt={benefit.name}
                    />
                    <h5>{benefit.name}</h5>
                    <p>{benefit.description}</p>
                  </a>
                </Link>
              ))}
            </Container>
          </>
        )}
      </div>
      <CurvedFooter />
    </div>
  );
};

export default CoverageDetailsTemplate;
